<template>
  <div class="card-content">
    <div class="signin-form row justify-content-center p-0 m-0">
      <form class="form-group col-8 pl-3 pr-3 mt-auto mb-4" @submit.prevent="signin">
        <div class="mb-4 mt-5">
          <h1 class="m-0 text-primary system-title">Sistema Financeiro</h1>
        </div>

        <div class="mb-3" :class="{ 'has-error': $v.username.$invalid && $v.username.$dirty }">
          <label class="mb-2 form-label-bold">Email:</label>
          <input
            id="username"
            v-model="username"
            class="form-control text-center"
            type="text"
            @input="$v.username.$touch()"
          />
          <small v-if="$v.username.$invalid && $v.username.$dirty" class="text-danger"
            >E-mail inválido.</small
          >
        </div>

        <div class="mb-4" :class="{ 'has-error': $v.password.$invalid && $v.password.$dirty }">
          <label class="mb-2 form-label-bold">Senha:</label>
          <input
            id="password"
            v-model="password"
            class="form-control text-center"
            type="password"
            @input="$v.password.$touch()"
          />
          <small v-if="$v.password.$invalid && $v.password.$dirty" class="text-danger"
            >Senha obrigatória.</small
          >
        </div>

        <!-- reCAPTCHA checkbox -->
        <div class="mb-3 text-center">
          <div ref="recaptchaContainer"></div>
          <small v-if="recaptchaError" class="text-danger">
            Confirme o reCAPTCHA antes de entrar.
          </small>
        </div>

        <div class="text-center mb-2">
          <button type="submit" class="btn btn-primary col-sm-12 col-md-6 mt-3 mb-3">Entrar</button>
        </div>

        <div class="text-center mb-4">
          <router-link to="/esqueci-minha-senha" class="forgot-password-link">
            Esqueci minha senha.
          </router-link>
        </div>
      </form>
    </div>
    <div class="signin-footer row justify-content-center pl-3 pr-3 mb-4" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Swal from 'sweetalert2';
import FinancialApi from '@/services/FinancialApi';
import Alert from '@/models/Alert';
import { useRecaptcha } from '@/composables/useRecaptcha';

const username = ref('');
const password = ref('');
const router = useRouter();
const store = useStore();

const { recaptchaContainer, recaptchaError, getRecaptchaToken } = useRecaptcha();

const rules = computed(() => ({
  username: { required, email },
  password: { required },
}));

const $v = useVuelidate(rules, { username, password });

async function signin() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    return;
  }

  const recaptchaToken = getRecaptchaToken();
  if (recaptchaError.value) {
    return;
  }

  const api = new FinancialApi();

  window.$bus.emit('spinner-run');

  const response = await api.signin(username.value, password.value, recaptchaToken);
  window.$bus.emit('spinner-stop');

  if (response.status === 401) {
    const alert = new Alert();
    alert.title = 'Ops';
    alert.error = 'E-mail e/ou senha estão inválidos.';
    alert.confirmButtonText = 'OK';
    window.$bus.emit('alert-error', alert);
    return;
  }

  if (
    response.status === 422 &&
    (response.data.error === 'NEED_CHANGE_PASSWORD' ||
      response.data.message?.includes('NEED_CHANGE_PASSWORD'))
  ) {
    const result = await Swal.fire({
      icon: 'error',
      title: 'Senha expirada!',
      text: 'Para obter acesso ao sistema será necessário redefinir sua senha.',
      confirmButtonText: 'Redefinir senha',
    });

    if (result.isConfirmed) {
      router.push(`/alterar-senha-expirada/${username.value}`);
    }
    return;
  }

  store.dispatch('account/login', response.data);
  router.push('/');
}
</script>

<style scoped>
.system-title {
  font-weight: 900 !important;
  font-size: 2.5rem !important;
  letter-spacing: -0.5px;
}

.forgot-password-link {
  text-decoration: none !important;
  color: var(--bs-primary, #ff7a00);
  font-size: 0.9rem;
  font-weight: 700 !important;
  transition: color 0.2s ease;
}

.forgot-password-link:hover {
  text-decoration: none !important;
  color: var(--bs-primary-dark, #e56900);
}

.form-label-bold {
  font-weight: 700 !important;
}
</style>
